import { useState } from 'react';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useNavigate, Link } from 'react-router-dom';

import './reset-password.css';

import { onError } from '../../../utils/error.util';
import { checkPasswordRequirement, validatePassword } from '../../../utils/password.util';

import Alert from '@mui/material/Alert';
import { BsArrowRepeat } from 'react-icons/bs';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  function validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  function getErrorMessage(error: any): string {
    const errorMessage = error?.message || error?.toString() || '';
    const errorCode = error?.name || error?.code || '';

    // Handle specific Cognito error codes
    if (errorCode === 'UserNotFoundException' || errorMessage.includes('User does not exist')) {
      // Security: Don't reveal if user exists - use generic message
      return 'If an account exists with this email, a password reset code has been sent.';
    }
    if (errorCode === 'CodeMismatchException' || errorMessage.includes('Invalid verification code')) {
      return 'Invalid verification code. Please check your email and try again.';
    }
    if (errorCode === 'ExpiredCodeException' || errorMessage.includes('Code has expired')) {
      return 'The verification code has expired. Please request a new code.';
    }
    if (errorCode === 'LimitExceededException' || errorMessage.includes('Too many attempts')) {
      return 'Too many attempts. Please try again later.';
    }
    if (errorCode === 'InvalidPasswordException' || errorMessage.includes('Password did not conform')) {
      return 'Password does not meet requirements. Please check the password requirements below.';
    }
    if (errorMessage.includes('Network') || errorMessage.includes('Failed to fetch')) {
      return 'Network error. Please check your internet connection and try again.';
    }

    // Return the error message if it's user-friendly, otherwise return a generic message
    if (errorMessage && !errorMessage.includes('AWS') && !errorMessage.includes('Cognito')) {
      return errorMessage;
    }

    return 'An error occurred. Please try again.';
  }

  async function handleRequestReset(event: any) {
    event.preventDefault();

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    // Validate email format
    if (!validateEmail(email)) {
      setErrorMessage('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    try {
      await resetPassword({ username: email });
      // Security: Always show success message, don't reveal if user exists
      setSuccessMessage('If an account exists with this email, a password reset code has been sent.');
      setStep('confirm');
    } catch (e: any) {
      onError(e);
      // For UserNotFoundException, show generic success message for security
      if (e?.name === 'UserNotFoundException') {
        setSuccessMessage('If an account exists with this email, a password reset code has been sent.');
        setStep('confirm');
      } else {
        setErrorMessage(getErrorMessage(e));
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleConfirmReset(event: any) {
    event.preventDefault();

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password requirements
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setErrorMessage(passwordError);
      setIsLoading(false);
      return;
    }

    // Validate confirmation code
    if (!confirmationCode || confirmationCode.length === 0) {
      setErrorMessage('Please enter the verification code');
      setIsLoading(false);
      return;
    }

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: confirmationCode,
        newPassword: newPassword,
      });
      setSuccessMessage('Password reset successful! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (e: any) {
      onError(e);
      setErrorMessage(getErrorMessage(e));
      setIsLoading(false);
    }
  }

  function handleResendCode() {
    setStep('request');
    setConfirmationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setErrorMessage('');
    setSuccessMessage('');
  }

  return (
    <div id="login-section">
      <div id="bg"></div>
      <section id="login">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 mx-auto my-auto">
              <img alt="TSF" id="tsf-logo" src="/assets/images/trailer-solutions-fl-logo.png" />
            </div>
            <div className="col-12">
              <form onSubmit={step === 'request' ? handleRequestReset : handleConfirmReset}>
                {errorMessage && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMessage}
                  </Alert>
                )}
                {successMessage && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {successMessage}
                  </Alert>
                )}
                {step === 'request' ? (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Enter your email address and we'll send you a verification code to reset your password.
                    </Alert>
                    <label className="d-none">Email</label>
                    <input
                      className="form-control mb-2"
                      required
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      placeholder="Email Address"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errorMessage) setErrorMessage('');
                      }}
                    />
                    <button id="submit-btn" type="submit" disabled={!email || !validateEmail(email) || isLoading}>
                      {isLoading && <BsArrowRepeat className="spinning" />}
                      Send Reset Code
                    </button>
                    <div className="text-center mt-3">
                      <Link to="/login" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.875rem' }}>
                        Back to Login
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    <label className="d-none">Verification Code</label>
                    <input
                      className="form-control mb-2"
                      required
                      id="confirmationCode"
                      name="confirmationCode"
                      type="text"
                      autoComplete="one-time-code"
                      autoFocus
                      placeholder="Verification Code"
                      value={confirmationCode}
                      onChange={(e) => {
                        setConfirmationCode(e.target.value);
                        if (errorMessage) setErrorMessage('');
                      }}
                    />
                    <label className="d-none">New Password</label>
                    <div className="position-relative mb-2">
                      <input
                        className="form-control"
                        required
                        id="newPassword"
                        name="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="New Password"
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          if (errorMessage) setErrorMessage('');
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-link position-absolute"
                        style={{
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          padding: '0',
                          border: 'none',
                          background: 'none',
                          textDecoration: 'none',
                        }}
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                    {newPassword && (
                      <div className="mb-3" style={{ fontSize: '0.75rem', color: '#666' }}>
                        <div style={{ marginBottom: '0.25rem' }}>
                          {checkPasswordRequirement(newPassword, 'length') ? '✓' : '✗'} At least 8 characters
                        </div>
                        <div style={{ marginBottom: '0.25rem' }}>
                          {checkPasswordRequirement(newPassword, 'uppercase') ? '✓' : '✗'} At least one uppercase letter
                        </div>
                        <div style={{ marginBottom: '0.25rem' }}>
                          {checkPasswordRequirement(newPassword, 'lowercase') ? '✓' : '✗'} At least one lowercase letter
                        </div>
                        <div style={{ marginBottom: '0.25rem' }}>
                          {checkPasswordRequirement(newPassword, 'number') ? '✓' : '✗'} At least one number
                        </div>
                        <div style={{ marginBottom: '0.25rem' }}>
                          {checkPasswordRequirement(newPassword, 'special') ? '✓' : '✗'} At least one special character
                        </div>
                      </div>
                    )}
                    <label className="d-none">Confirm Password</label>
                    <div className="position-relative mb-2">
                      <input
                        className="form-control"
                        required
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (errorMessage) setErrorMessage('');
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-link position-absolute"
                        style={{
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          padding: '0',
                          border: 'none',
                          background: 'none',
                          textDecoration: 'none',
                        }}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                    {confirmPassword && (
                      <div className="mb-3" style={{ fontSize: '0.75rem' }}>
                        {newPassword === confirmPassword ? (
                          <span style={{ color: 'green' }}>✓ Passwords match</span>
                        ) : (
                          <span style={{ color: 'red' }}>✗ Passwords do not match</span>
                        )}
                      </div>
                    )}
                    <button
                      id="submit-btn"
                      type="submit"
                      disabled={!confirmationCode || !newPassword || !confirmPassword || isLoading}
                    >
                      {isLoading && <BsArrowRepeat className="spinning" />}
                      Reset Password
                    </button>
                    <div className="text-center mt-3">
                      <button
                        type="button"
                        onClick={handleResendCode}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'inherit',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          padding: 0,
                          marginBottom: '0.5rem',
                          display: 'block',
                          width: '100%',
                        }}
                      >
                        Resend Code
                      </button>
                      <Link to="/login" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.875rem' }}>
                        Back to Login
                      </Link>
                    </div>
                  </>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
